import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";

export default function Login() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const resetToken = params.get("reset");

  const [mode, setMode] = useState(resetToken ? "reset" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resetLink, setResetLink] = useState("");
  const [newPw, setNewPw] = useState("");

  useEffect(() => {
    if (resetToken) setMode("reset");
  }, [resetToken]);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password, remember);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(errMsg(err, "Anmeldung fehlgeschlagen"));
    } finally {
      setBusy(false);
    }
  };

  const doForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setResetLink(data.reset_link);
      toast.success(data.message);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token: resetToken, password: newPw });
      toast.success("Passwort wurde geändert. Bitte anmelden.");
      setParams({});
      setMode("login");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-[#11152F]" data-testid="login-page">
      {/* Brand panel */}
      <div className="relative hidden flex-1 flex-col justify-between overflow-hidden p-12 lg:flex">
        <img src="/assets/login-bg.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#11152F]/75 via-[#11152F]/55 to-[#11152F]/90" />
        <img src="/assets/logo-white.png" alt="Manuela Gassner" className="relative h-12 w-auto self-start" />
        <div className="relative">
          <img src="/assets/logo-mark.png" alt="" className="mb-8 h-28 w-28 self-start opacity-90" />
          <h1 className="font-display text-4xl font-bold leading-tight text-white xl:text-5xl">
            Agentur<br />Control Center
          </h1>
          <p className="mt-4 max-w-md text-base leading-relaxed text-[#A3AED0]">
            Privater Arbeitsbereich für Manuela Gassner & Mondher Ghali.
            Projekte, Aufgaben, Dateien, Zugänge und Zahlungen – alles an einem Ort.
          </p>
        </div>
        <p className="relative text-xs tracking-wide text-[#A3AED0]/60">
          Interner Bereich · Nur für autorisierte Personen
        </p>
      </div>

      {/* Form panel */}
      <div className="flex w-full items-center justify-center px-6 py-10 lg:w-[520px] lg:bg-[#F7F6F2]">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="mb-8 flex flex-col items-center lg:hidden">
            <img src="/assets/logo-white.png" alt="Manuela Gassner" className="h-10 w-auto" />
          </div>
          <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-md lg:border-[#1D225C]/10 lg:bg-white lg:shadow-xl lg:shadow-[#1D225C]/5">
            <div className="mb-6 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#B88A4A]/15">
                <Lock className="h-5 w-5 text-[#B88A4A]" />
              </span>
              <div>
                <h2 className="font-display text-lg font-bold text-white lg:text-[#1D225C]">
                  {mode === "login" && "Anmelden"}
                  {mode === "forgot" && "Passwort vergessen"}
                  {mode === "reset" && "Neues Passwort"}
                </h2>
                <p className="text-xs text-[#A3AED0] lg:text-[#777]">
                  {mode === "login" && "Willkommen zurück im Control Center"}
                  {mode === "forgot" && "Zurücksetzen-Link anfordern"}
                  {mode === "reset" && "Neues Passwort festlegen"}
                </p>
              </div>
            </div>

            {error && (
              <p data-testid="login-error" className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300 lg:text-red-600">
                {error}
              </p>
            )}

            {mode === "login" && (
              <form onSubmit={doLogin} className="space-y-4">
                <div>
                  <Label className="text-[#A3AED0] lg:text-[#1D225C]">E-Mail</Label>
                  <Input
                    data-testid="login-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@agentur.at"
                    className="mt-1.5 h-11 rounded-xl border-white/10 bg-white/5 text-white placeholder:text-white/30 lg:border-[#1D225C]/15 lg:bg-white lg:text-[#1D225C]"
                  />
                </div>
                <div>
                  <Label className="text-[#A3AED0] lg:text-[#1D225C]">Passwort</Label>
                  <div className="relative mt-1.5">
                    <Input
                      data-testid="login-password-input"
                      type={showPw ? "text" : "password"}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="h-11 rounded-xl border-white/10 bg-white/5 pr-10 text-white placeholder:text-white/30 lg:border-[#1D225C]/15 lg:bg-white lg:text-[#1D225C]"
                    />
                    <button
                      type="button"
                      data-testid="login-toggle-password"
                      onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 lg:text-[#777]"
                    >
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-[#A3AED0] lg:text-[#555]">
                    <Checkbox
                      data-testid="login-remember-checkbox"
                      checked={remember}
                      onCheckedChange={(v) => setRemember(!!v)}
                    />
                    Angemeldet bleiben
                  </label>
                  <button
                    type="button"
                    data-testid="forgot-password-link"
                    onClick={() => { setMode("forgot"); setError(""); }}
                    className="text-sm font-medium text-[#B88A4A] hover:underline"
                  >
                    Passwort vergessen?
                  </button>
                </div>
                <Button
                  data-testid="login-submit-button"
                  type="submit"
                  disabled={busy}
                  className="h-11 w-full rounded-xl bg-[#B88A4A] text-sm font-semibold text-white transition-colors hover:bg-[#a67a3f]"
                >
                  {busy ? "Anmelden…" : "Anmelden"}
                </Button>
              </form>
            )}

            {mode === "forgot" && (
              <form onSubmit={doForgot} className="space-y-4">
                <div>
                  <Label className="text-[#A3AED0] lg:text-[#1D225C]">E-Mail</Label>
                  <Input
                    data-testid="forgot-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1.5 h-11 rounded-xl border-white/10 bg-white/5 text-white lg:border-[#1D225C]/15 lg:bg-white lg:text-[#1D225C]"
                  />
                </div>
                {resetLink && (
                  <div className="rounded-xl border border-[#B88A4A]/30 bg-[#B88A4A]/10 px-3 py-2.5" data-testid="reset-link-box">
                    <p className="text-xs text-[#A3AED0] lg:text-[#555]">Dein persönlicher Link (1 Stunde gültig):</p>
                    <button
                      type="button"
                      data-testid="reset-link-button"
                      onClick={() => { window.location.href = resetLink; }}
                      className="mt-1 break-all text-left text-sm font-semibold text-[#B88A4A] hover:underline"
                    >
                      Passwort jetzt zurücksetzen →
                    </button>
                  </div>
                )}
                <Button data-testid="forgot-submit-button" type="submit" disabled={busy} className="h-11 w-full rounded-xl bg-[#B88A4A] text-sm font-semibold text-white hover:bg-[#a67a3f]">
                  {busy ? "Senden…" : "Link anfordern"}
                </Button>
                <button type="button" data-testid="back-to-login" onClick={() => { setMode("login"); setError(""); setResetLink(""); }} className="w-full text-center text-sm text-[#A3AED0] hover:text-white lg:text-[#777]">
                  ← Zurück zur Anmeldung
                </button>
              </form>
            )}

            {mode === "reset" && (
              <form onSubmit={doReset} className="space-y-4">
                <div>
                  <Label className="text-[#A3AED0] lg:text-[#1D225C]">Neues Passwort (min. 8 Zeichen)</Label>
                  <Input
                    data-testid="reset-password-input"
                    type="password"
                    required
                    minLength={8}
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    className="mt-1.5 h-11 rounded-xl border-white/10 bg-white/5 text-white lg:border-[#1D225C]/15 lg:bg-white lg:text-[#1D225C]"
                  />
                </div>
                <Button data-testid="reset-submit-button" type="submit" disabled={busy} className="h-11 w-full rounded-xl bg-[#B88A4A] text-sm font-semibold text-white hover:bg-[#a67a3f]">
                  {busy ? "Speichern…" : "Passwort speichern"}
                </Button>
              </form>
            )}
          </div>
          <p className="mt-6 text-center text-xs text-[#A3AED0]/50 lg:text-[#999]">
            © {new Date().getFullYear()} Manuela Gassner · Webdesign
          </p>
        </div>
      </div>
    </div>
  );
}
